import { MorphKey } from '../../../types/appearance';

// Mini body silhouette that highlights the region a morph slider affects —
// answers "what does this slider change?" at a glance. Parts are simple
// rounded shapes; highlighted parts fill with the primary color, the rest
// stay muted.

type BodyPart =
  | 'head' | 'shoulderL' | 'shoulderR' | 'torso'
  | 'upperArmL' | 'upperArmR' | 'forearmL' | 'forearmR'
  | 'thighL' | 'thighR' | 'calfL' | 'calfR';

const ALL_PARTS: BodyPart[] = [
  'head', 'shoulderL', 'shoulderR', 'torso',
  'upperArmL', 'upperArmR', 'forearmL', 'forearmR',
  'thighL', 'thighR', 'calfL', 'calfR',
];

const ARMS: BodyPart[] = ['upperArmL', 'upperArmR', 'forearmL', 'forearmR'];
const LEGS: BodyPart[] = ['thighL', 'thighR', 'calfL', 'calfR'];

const MORPH_REGIONS: Record<MorphKey, BodyPart[]> = {
  height: ALL_PARTS,
  bodyScale: ['shoulderL', 'shoulderR', 'torso', ...ARMS, ...LEGS],
  muscleValue: ['torso', ...ARMS],
  buildValue: ['torso'],
  feminineValue: ['torso', 'thighL', 'thighR'],
  legLength: LEGS,
  armLength: ARMS,
  thighSize: ['thighL', 'thighR'],
  bicepsSize: ['upperArmL', 'upperArmR'],
  shoulderWidth: ['shoulderL', 'shoulderR'],
  torsoWidth: ['torso'],
  headSize: ['head'],
};

// Directional hint arrows: vertical for length/height morphs, horizontal for widths.
const VERTICAL_HINT: MorphKey[] = ['height', 'legLength', 'armLength'];
const HORIZONTAL_HINT: MorphKey[] = ['bodyScale', 'shoulderWidth', 'torsoWidth'];

const SHAPES: Record<BodyPart, { x: number; y: number; w: number; h: number; r: number } | { cx: number; cy: number; cr: number }> = {
  head: { cx: 17, cy: 5.5, cr: 4.2 },
  shoulderL: { x: 8.6, y: 11, w: 4.6, h: 4.4, r: 2 },
  shoulderR: { x: 20.8, y: 11, w: 4.6, h: 4.4, r: 2 },
  torso: { x: 11.6, y: 11, w: 10.8, h: 13.6, r: 2.6 },
  upperArmL: { x: 4.8, y: 12.4, w: 3.4, h: 7.2, r: 1.7 },
  upperArmR: { x: 25.8, y: 12.4, w: 3.4, h: 7.2, r: 1.7 },
  forearmL: { x: 4.8, y: 20.2, w: 3.4, h: 6.6, r: 1.7 },
  forearmR: { x: 25.8, y: 20.2, w: 3.4, h: 6.6, r: 1.7 },
  thighL: { x: 11.8, y: 25.2, w: 4.6, h: 8.4, r: 2 },
  thighR: { x: 17.6, y: 25.2, w: 4.6, h: 8.4, r: 2 },
  calfL: { x: 12.2, y: 34.2, w: 3.9, h: 7.8, r: 1.9 },
  calfR: { x: 17.9, y: 34.2, w: 3.9, h: 7.8, r: 1.9 },
};

interface MorphIconProps {
  morphKey: MorphKey;
}

/** Small/large isolated-region glyph for the slider's min/max ends: renders
 * ONLY the body parts a morph affects, scaled to fit a 16×16 box. `end="min"`
 * draws them small, `end="max"` large — an instant "less ↔ more of this". */
export function RegionGlyph({ morphKey, end }: { morphKey: MorphKey; end: 'min' | 'max' }) {
  const parts = MORPH_REGIONS[morphKey] ?? [];

  // Bounding box of the region shapes in MorphIcon space
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  parts.forEach((part) => {
    const s = SHAPES[part];
    if ('cx' in s) {
      minX = Math.min(minX, s.cx - s.cr); maxX = Math.max(maxX, s.cx + s.cr);
      minY = Math.min(minY, s.cy - s.cr); maxY = Math.max(maxY, s.cy + s.cr);
    } else {
      minX = Math.min(minX, s.x); maxX = Math.max(maxX, s.x + s.w);
      minY = Math.min(minY, s.y); maxY = Math.max(maxY, s.y + s.h);
    }
  });
  const bw = Math.max(maxX - minX, 0.001);
  const bh = Math.max(maxY - minY, 0.001);

  const BOX = 16;
  const PAD = 1.2;
  const fit = (BOX - PAD * 2) / Math.max(bw, bh);
  const scale = fit * (end === 'min' ? 0.55 : 1);
  // Center the scaled region in the box
  const ox = (BOX - bw * scale) / 2 - minX * scale;
  const oy = (BOX - bh * scale) / 2 - minY * scale;

  return (
    <svg
      className={`region-glyph region-glyph-${end}`}
      viewBox={`0 0 ${BOX} ${BOX}`}
      width={BOX}
      height={BOX}
      aria-hidden="true"
      focusable="false"
    >
      <g transform={`translate(${ox} ${oy}) scale(${scale})`}>
        {parts.map((part) => {
          const s = SHAPES[part];
          if ('cx' in s) {
            return <circle key={part} className="region-glyph-shape" cx={s.cx} cy={s.cy} r={s.cr} />;
          }
          return (
            <rect key={part} className="region-glyph-shape" x={s.x} y={s.y} width={s.w} height={s.h} rx={s.r} />
          );
        })}
      </g>
    </svg>
  );
}

export function MorphIcon({ morphKey }: MorphIconProps) {
  const highlighted = new Set(MORPH_REGIONS[morphKey] ?? []);

  return (
    <svg
      className="morph-icon"
      viewBox="0 0 34 44"
      width="26"
      height="34"
      aria-hidden="true"
      focusable="false"
    >
      {ALL_PARTS.map((part) => {
        const s = SHAPES[part];
        const cls = highlighted.has(part) ? 'morph-icon-hl' : 'morph-icon-bg';
        if ('cx' in s) {
          return <circle key={part} className={cls} cx={s.cx} cy={s.cy} r={s.cr} />;
        }
        return (
          <rect key={part} className={cls} x={s.x} y={s.y} width={s.w} height={s.h} rx={s.r} />
        );
      })}
      {VERTICAL_HINT.includes(morphKey) && (
        <path className="morph-icon-hint" d="M1.6 16 L1.6 28 M0 17.8 L1.6 15.6 L3.2 17.8 M0 26.2 L1.6 28.4 L3.2 26.2" />
      )}
      {HORIZONTAL_HINT.includes(morphKey) && (
        <path className="morph-icon-hint" d="M11 42.6 L23 42.6 M12.8 41 L10.6 42.6 L12.8 44.2 M21.2 41 L23.4 42.6 L21.2 44.2" transform="translate(0,-1.2)" />
      )}
    </svg>
  );
}

export default MorphIcon;
